import { types, flow, cast, getRoot } from 'mobx-state-tree';
import qs from 'qs';
import * as R from 'ramda';

import { ApiStore } from '@src/features/common';
import { IRouterStore } from '@src/models';
import { ROUTES } from 'src/constants';
import { capitalizeFirstLetter } from 'src/helpers';

export const JOBS_PER_PAGE = 20;

export type storeEnum = 'jobsList' | 'learningList' | 'magazineList' | 'learningTopPicksList';

const Views = (self: any) => ({
  get getPrice() {
    return self.filters?.price || {};
  }
});

export const Feed = types
  .model({})
  .actions(self => {
    const routerStore = getRoot<{ routerStore: IRouterStore }>(self).routerStore;

    const getEntries = (entries, detectIsChecked) => {
      const filters = {};
      Object.entries(entries).forEach(
        ([key, items]: [string, Array<string>]) =>
          (filters[key] = Array.isArray(items)
            ? items.map(item => {
                const generateOwnedByTitle = () => {
                  // if item has name then use it otherwise use email
                  if (item.first_name && item.last_name) {
                    return `${capitalizeFirstLetter(item.first_name)} ${capitalizeFirstLetter(item.last_name)}`;
                  } else {
                    return item.email;
                  }
                };
                const isItemWithId = Array.isArray(item);
                const isItemWithIdObj = item.id;
                const label = isItemWithId ? item[1] : isItemWithIdObj ? generateOwnedByTitle() : item;

                return {
                  label,
                  checked: detectIsChecked(key, label, item),
                  ...(isItemWithId
                    ? { value: String(item[0]) }
                    : isItemWithIdObj
                    ? { value: item.id }
                    : { value: item })
                };
              })
            : items)
      );

      return filters;
    };

    const checkIsFiltersOn = () => {
      const searchRaw = getRoot<{ routerStore: IRouterStore }>(self).routerStore.location.search;
      // ?scope=all means no filter at /candidates/roles page
      const { expandedJobIdsList, scope, client_id, ...searchObj } = qs.parse(searchRaw, { ignoreQueryPrefix: true });
      const search = qs.stringify(searchObj, { addQueryPrefix: true, arrayFormat: 'brackets' });

      return search.length > 1;
    };

    const clearFilters = () => {
      const pathname = getRoot<{ routerStore: IRouterStore }>(self).routerStore.location.pathname;

      const path = [
        ROUTES.candidatesRoutes.learning,
        ROUTES.candidatesRoutes.learning + '/',
        ROUTES.resources,
        ROUTES.resources + '/'
      ].find(path => path === pathname)
        ? 'learningScope'
        : 'scope';
      const scope = self[path];

      const newQs = qs.stringify({ scope: scope?.value });

      routerStore.push({
        search: [
          ROUTES.candidatesRoutes.learning,
          ROUTES.candidatesRoutes.learning + '/',
          ROUTES.resources,
          ROUTES.resources + '/',
          ROUTES.candidatesRoutes.roles
        ].find(path => path === pathname)
          ? newQs
          : ''
      });
    };

    const updateFilters = (newFilters, search) => {
      // if search '' that means all all filters just goes straight off
      if (search === '' || search === '?scope=all') {
        self.filters = getEntries(newFilters, () => false);
        return;
      }

      const filterQueryString = qs.parse(search, {
        ignoreQueryPrefix: true
      });
      // this will get throught all new accordion filters and mark them checked unchcked
      // depends on fn callback
      self.filters = getEntries(newFilters, (key, label, item) =>
        // check if in query string
        Boolean(
          Array.isArray(filterQueryString[key]) &&
            filterQueryString[key].find(v => {
              return v === label || Number(v) === item?.id || Number(v) === item?.[0];
            })
        )
      );
    };

    const fetchJobs = flow(function* fetch(
      getJobs: (qs: string) => string,
      search = '',
      options = { blowPrevRes: false, store: 'jobsList', customPath: null }
    ) {
      try {
        const { blowPrevRes, store } = options;

        self[`is${store}Fetched`] = false;
        if (blowPrevRes) {
          self[store] = cast([]);
          self.currentPage = 1;
        }

        const res = yield self.fetch(
          getJobs(
            qs.stringify(
              { page: self.currentPage, per_page: JOBS_PER_PAGE, ...qs.parse(search, { ignoreQueryPrefix: true }) },
              { addQueryPrefix: true, arrayFormat: 'brackets' }
            )
          )
        );
        self[store] = cast(R.uniq([...self[store], ...res.entities]));

        updateFilters(res.filters || {}, search);

        self[`is${store}Fetched`] = true;
        return res;
      } catch (error) {}
    });

    const setFilter = (id, value) => {
      const updateFilter = (id, value) => ({
        [id]: self.filters[id].map(item => ({
          ...item,
          label: item.label,
          checked: item.value === value ? !item.checked : item.checked
        }))
      });
      // add filter to query string
      // refetch Jobs (this will also refetch filters and update them)
      const rootStore = getRoot<{ routerStore: IRouterStore }>(self);

      // add values to the store
      const updatedFilters = updateFilter(id, value);
      const filters = {
        ...self.filters,
        ...updatedFilters
      };
      self.filters = filters;

      // add values to the url
      const newQsObj = qs.parse(rootStore.routerStore.location.search, { ignoreQueryPrefix: true });
      const newQs = qs.stringify(
        // ..{ [id]: updatedFilters[id].filter(({ checked }) => checked).map(({ value }) => value) }
        // will be somthing like {key: [value1, value2]}, values are checked filters
        { ...newQsObj, ...{ [id]: updatedFilters[id].filter(({ checked }) => checked).map(({ value }) => value) } },
        { arrayFormat: 'brackets' }
      );
      rootStore.routerStore.push({
        search: newQs
      });
    };

    const setScope = (newScope, path = 'scope') => {
      self[path] = newScope;
    };

    // ToDo

    return {
      incPage: () => {
        self.currentPage = self.currentPage + 1;
      },
      setFilter,
      checkIsFiltersOn,
      setScope,
      clearFilters,
      updateFilters,
      getEntries,
      fetchJobs,
      clearData: (store: storeEnum) => {
        self[`is${store}Fetched`] = false;
        self.filters = {};
        self.currentPage = 1;
        self[store] = cast([]);
      }
    };
  })
  .views(Views);

export const Jobs = types.compose(
  ApiStore,
  Feed,
  types.model({
    // dropdown filter
    scope: types.frozen({ value: 'all', label: 'All roles' }),
    // accordion filters
    filters: types.frozen(),
    // price filter
    price: types.frozen(),
    learningScope: types.frozen(
      new URLSearchParams(window.location.search).get('scope') === 'suggested'
        ? { value: 'suggested', label: 'Resources matching my profile' }
        : { value: 'all', label: 'All resources' }
    ),

    // data
    currentPage: 1,
    // my roles / jobs page
    jobsList: types.array(types.frozen()),
    isjobsListFetched: false,
    // magazine page
    magazineList: types.array(types.frozen()),
    ismagazineListFetched: false,
    // learning / resources page
    learningList: types.array(types.frozen()),
    islearningListFetched: false,
    learningTopPicksList: types.array(types.frozen()),
    islearningTopPicksListFetched: false
  })
);

export const FeedStore = types.model({
  jobs: Jobs
});
